import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import EmptyState from "../../../../components/EmptyState";
import { SectionLoader } from "../../../../components/PageLoader";
import { getTransactions } from "../../../../redux/actions/walletActions";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";

const TransactionHistory = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [page] = useState(1);
  const [loading, setLoading] = useState(false);

  const { transactions } = useSelector((state) => state.wallet);
  let items = [];
  if (Array.isArray(transactions)) {
    items = Array.isArray(transactions[0]) ? transactions[0] : transactions;
  } else if (transactions && Array.isArray(transactions["0"])) {
    items = transactions["0"];
  }

  useEffect(() => {
    setLoading(true);
    Promise.resolve(getTransactions(dispatch, page)).finally(() =>
      setLoading(false)
    );
  }, [dispatch, page]);

  // Group transactions by date
  const grouped = items.reduce((acc, tx) => {
    const day = moment(tx.date_created).format("DD MMM YYYY");
    if (!acc[day]) acc[day] = [];
    acc[day].push(tx);
    return acc;
  }, {});

  return (
    <div className={classes.root}>

      {/* Header */}
      <div className={classes.header}>
        <div>
          <div className={classes.title}>Transaction History</div>
          <div className={classes.subtitle}>
            {items.length > 0 ? `${items.length} transactions` : "All your past transactions"}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && <SectionLoader />}

      {/* Empty */}
      {!loading && items.length === 0 && (
        <div className={classes.emptyWrap}>
          <EmptyState text="No transactions yet" />
        </div>
      )}

      {/* Transaction groups */}
      {!loading && items.length > 0 && (
        <div className={classes.list}>
          {Object.entries(grouped).map(([day, txs]) => (
            <div key={day}>

              {/* Day label */}
              <div className={classes.dayLabel}>{day}</div>

              {/* Transactions for this day */}
              <div className={classes.group}>
                {txs.map((transaction, idx) => {
                  const isCredit = transaction.transactionType === "credit";
                  return (
                    <div
                      key={transaction.id}
                      className={classes.row}
                      style={{
                        borderBottom:
                          idx < txs.length - 1
                            ? "1px solid #F3F4F6"
                            : "none",
                      }}
                    >
                      {/* Left: icon + info */}
                      <div className={classes.rowLeft}>
                        <div
                          className={classes.iconWrap}
                          style={{
                            background: isCredit ? "#EDFAF4" : "#FEF2F2",
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            {isCredit ? (
                              <path
                                d="M8 13V3M3 8l5-5 5 5"
                                stroke="#16A34A"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            ) : (
                              <path
                                d="M8 3v10M3 8l5 5 5-5"
                                stroke="#DC2626"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            )}
                          </svg>
                        </div>
                        <div>
                          <div className={classes.desc}>
                            {transaction.description}
                          </div>
                          <div className={classes.time}>
                            {moment(transaction.date_created).format("h:mm a")}
                          </div>
                        </div>
                      </div>

                      {/* Right: amount */}
                      <div
                        className={classes.amount}
                        style={{ color: isCredit ? "#16A34A" : "#DC2626" }}
                      >
                        {isCredit ? "+" : "−"}
                        {transaction.amount}{" "}
                        <span className={classes.currency}>
                          {transaction.currency}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  root: {
    padding: "24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  title: {
    fontSize: "17px",
    fontWeight: 600,
    color: "#032250",
  },
  subtitle: {
    fontSize: "13px",
    color: "#7F91A7",
    marginTop: "3px",
  },
  emptyWrap: {
    paddingTop: "40px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  dayLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#7F91A7",
    marginBottom: "8px",
    paddingLeft: "4px",
  },
  group: {
    background: "#FFFFFF",
    border: "1px solid #EFEFEF",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0px 1px 4px rgba(0,0,0,0.05)",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    transition: "background 0.15s",
    "&:hover": {
      background: "#F9FAFB",
    },
  },
  rowLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  iconWrap: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  desc: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#032250",
    maxWidth: "280px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  time: {
    fontSize: "12px",
    color: "#7F91A7",
    marginTop: "2px",
  },
  amount: {
    fontSize: "14px",
    fontWeight: 600,
    flexShrink: 0,
  },
  currency: {
    fontSize: "11px",
    fontWeight: 500,
    opacity: 0.75,
  },
});

export default TransactionHistory;